import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  LayoutDashboard,
  LayoutGrid,
  Landmark,
  Users,
  PlusCircle,
  Receipt,
  BookOpenText,
  AlertTriangle,
  CalendarRange,
  TrendingUp,
  HandCoins,
  LogOut,
  LogIn,
  Building2,
  ShieldCheck,
  Trash2,
  History,
  UserCog,
  Ban,
  Crown,
  UserCheck,
  PanelLeftClose,
  PanelLeftOpen,
  Percent,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { useLang } from "@/context/LanguageContext";

const BRANCH_OPTIONS_NOTE = "";

const ADMIN_ROLES = ["director", "manager", "admin"];

const NAV_GROUPS = [
  { id: "overview", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
    { to: "/director/overview", label: "All-Branch Overview", icon: LayoutGrid, id: "overview", adminOnly: true },
  ]},
  { id: "lending", items: [
    { to: "/loans", label: "Loans", icon: Landmark, id: "loans" },
    { to: "/customers", label: "Customers", icon: Users, id: "customers" },
    { to: "/loans/new", label: "New Loan", icon: PlusCircle, id: "new-loan", editOnly: true },
    { to: "/payments", label: "Payments", icon: Receipt, id: "payments" },
  ]},
  { id: "collections", items: [
    { to: "/collection", label: "Collection Priority", icon: AlertTriangle, id: "collection" },
    { to: "/bad-debt", label: "Bad Debt", icon: Ban, id: "bad-debt" },
    { to: "/collectors", label: "Collector Workbench", icon: UserCheck, id: "collectors" },
  ]},
  { id: "finance", items: [
    { to: "/ledger", label: "Branch Ledger", icon: BookOpenText, id: "ledger" },
    { to: "/reports/monthly", label: "Monthly Income", icon: CalendarRange, id: "monthly" },
    { to: "/commission", label: "Manager Commission", icon: Percent, id: "commission", adminOnly: true },
    { to: "/director/statement", label: "Director Statement", icon: Crown, id: "director-statement", adminOnly: true },
    { to: "/reports/interest", label: "Potential Interest", icon: TrendingUp, id: "interest" },
  ]},
  { id: "admin", items: [
    { to: "/reports/data-health", label: "Data Health", icon: ShieldCheck, id: "data-health" },
    { to: "/deleted-loans", label: "Deleted Loans", icon: Trash2, id: "deleted-loans" },
    { to: "/activity", label: "Activity Log", icon: History, id: "activity", adminOnly: true },
    { to: "/logins", label: "Login Records", icon: LogIn, id: "logins", adminOnly: true },
    { to: "/users", label: "Access Control", icon: ShieldCheck, id: "users", adminOnly: true },
  ]},
  { id: "settings", items: [
    { to: "/account", label: "Account", icon: UserCog, id: "account" },
  ]},
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { branch, setBranch, branches, canViewAll, currentBranchLabel } = useBranch();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [pendingBadDebt, setPendingBadDebt] = React.useState(0);

  React.useEffect(() => {
    if (!isAdmin) return undefined;
    let active = true;
    const fetchPending = () =>
      api
        .get("/bad-debt/requests", { params: { status: "pending", branch: "all" } })
        .then((r) => { if (active) setPendingBadDebt(Array.isArray(r.data) ? r.data.length : 0); })
        .catch(() => {});
    fetchPending();
    const t = setInterval(fetchPending, 60000);
    return () => { active = false; clearInterval(t); };
  }, [isAdmin]);
  const [collapsed, setCollapsed] = React.useState(() => {
    const stored = localStorage.getItem("sidebar_collapsed");
    if (stored !== null) return stored === "1";
    return typeof window !== "undefined" && window.innerWidth < 1024; // collapse by default on phones
  });

  const toggleSidebar = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem("sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  };

  const collapse = () => {
    localStorage.setItem("sidebar_collapsed", "1");
    setCollapsed(true);
  };

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Backspace" && e.key !== "Delete") return;
      const el = document.activeElement;
      const tag = el?.tagName?.toLowerCase();
      const isEditable =
        tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable;
      if (isEditable) return; // don't hijack while typing
      e.preventDefault();
      navigate(-1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className={`fixed top-0 left-0 h-screen w-64 border-r border-slate-200 bg-white flex flex-col z-20 transition-transform duration-300 ${collapsed ? "-translate-x-full" : "translate-x-0"}`}>
        <div className="px-5 h-16 flex items-center gap-2.5 border-b border-slate-200">
          <div className="h-9 w-9 rounded-md overflow-hidden">
            <img src="/icon-192.png?v=2" alt="Diamond Sharp" className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-slate-900 text-sm leading-tight">Diamond Sharp</div>
            <div className="text-[11px] text-slate-400 leading-tight truncate">{currentBranchLabel}</div>
          </div>
        </div>

        <div className="px-3 pt-3">
          {canViewAll ? (
            <select
              data-testid="branch-switcher"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-slate-900 outline-none cursor-pointer"
            >
              <option value="all">{t("sidebar.all_branches")}</option>
              {branches.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
            </select>
          ) : (
            <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-semibold text-slate-800 flex items-center gap-2" data-testid="branch-fixed">
              <Building2 className="h-4 w-4 text-slate-400" /> {currentBranchLabel}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter((item) => (!item.roles || item.roles.includes(user?.role)) && (!item.adminOnly || ADMIN_ROLES.includes(user?.role)) && (!item.editOnly || user?.canEdit));
            if (items.length === 0) return null;
            return (
              <div key={group.id} className="mb-1" data-testid={`nav-group-${group.id}`}>
                <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t(`nav.group.${group.id}`)}
                </div>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={`nav-${item.id}`}
                    onClick={collapse}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-slate-900 text-white"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }`
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{t(`nav.${item.id}`)}</span>
                    {item.id === "bad-debt" && isAdmin && pendingBadDebt > 0 && (
                      <span data-testid="bad-debt-pending-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
                        {pendingBadDebt}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
          <button
            data-testid="sidebar-collapse-button"
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center gap-2 px-3 py-3 mt-3 rounded-lg text-sm font-semibold text-white bg-slate-900 hover:bg-slate-700 border border-slate-900 shadow-sm transition-colors"
          >
            <PanelLeftClose className="h-5 w-5 shrink-0" />
            {t("sidebar.hide")}
          </button>
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center justify-center gap-1 mb-2 bg-slate-100 rounded-md p-1" data-testid="sidebar-lang-switch">
            <button
              data-testid="sidebar-lang-en"
              onClick={() => setLang("en")}
              className={`flex-1 px-2 py-1 rounded text-xs font-semibold transition-colors ${lang === "en" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
            >
              EN
            </button>
            <button
              data-testid="sidebar-lang-zh"
              onClick={() => setLang("zh")}
              className={`flex-1 px-2 py-1 rounded text-xs font-semibold transition-colors ${lang === "zh" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
            >
              中文
            </button>
          </div>
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
              {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{user?.name || t("sidebar.user")}</div>
              <div className="text-[11px] text-slate-400 truncate capitalize">{user?.role}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="text-slate-400 hover:text-red-600 transition-colors"
              title={t("sidebar.logout")}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {!collapsed && (
        <div
          data-testid="sidebar-backdrop"
          onClick={collapse}
          className="fixed inset-0 bg-black/40 z-10 lg:hidden"
        />
      )}

      {collapsed && (
        <button
          data-testid="sidebar-expand-button"
          onClick={toggleSidebar}
          title="Show sidebar"
          className="fixed top-1/2 -translate-y-1/2 left-0 z-30 h-14 w-9 rounded-r-xl bg-slate-900 text-white flex items-center justify-center shadow-lg hover:bg-slate-800 hover:w-11 transition-all"
        >
          <PanelLeftOpen className="h-5 w-5" />
        </button>
      )}

      <main className={`min-h-screen transition-all duration-300 ${collapsed ? "ml-0 pl-14" : "lg:ml-64"}`}>
        <div className="p-6 sm:p-8 max-w-[1500px] mx-auto fade-in">{children}</div>
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
